import React from 'react';
import type { PdfTemplateFooter } from '../../types/pdfTemplate';

interface PdfFooterSettingsProps {
  footer: PdfTemplateFooter;
  onChange: (footer: PdfTemplateFooter) => void;
}

const PdfFooterSettings: React.FC<PdfFooterSettingsProps> = ({ footer, onChange }) => {
  const update = (field: keyof PdfTemplateFooter, value: string | boolean) => {
    onChange({ ...footer, [field]: value });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-900">Footer Settings</h3>

      {/* Page Numbers */}
      <div className="flex items-center space-x-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={footer.showPageNumbers}
            onChange={e => update('showPageNumbers', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-brand-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
        </label>
        <span className="text-sm text-gray-700">Show page numbers</span>
      </div>

      {footer.showPageNumbers && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">Page Number Format</label>
          <select
            value={footer.pageNumberFormat}
            onChange={e => update('pageNumberFormat', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="Page X of Y">Page X of Y</option>
            <option value="X/Y">X/Y</option>
            <option value="X">X</option>
          </select>
        </div>
      )}

      {/* Generated Date */}
      <div className="flex items-center space-x-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={footer.showGeneratedDate}
            onChange={e => update('showGeneratedDate', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-brand-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
        </label>
        <span className="text-sm text-gray-700">Show generated date</span>
      </div>

      {/* Company Name in Footer */}
      <div className="flex items-center space-x-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={footer.showCompanyName}
            onChange={e => update('showCompanyName', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-brand-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
        </label>
        <span className="text-sm text-gray-700">Show company name in footer</span>
      </div>

      {/* Custom Text */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Custom Footer Text</label>
        <input
          type="text"
          value={footer.customText || ''}
          onChange={e => update('customText', e.target.value)}
          placeholder="Confidential - For Internal Use Only"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      {/* Footer Color */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Footer Color</label>
        <div className="flex items-center space-x-3">
          <input
            type="color"
            value={footer.footerColor}
            onChange={e => update('footerColor', e.target.value)}
            className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
          />
          <input
            type="text"
            value={footer.footerColor}
            onChange={e => update('footerColor', e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 font-mono"
          />
        </div>
      </div>
    </div>
  );
};

export default PdfFooterSettings;
